import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  PhotoIcon,
  PlusIcon,
  PaperAirplaneIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useWalletStore } from '../store/walletStore';
import { getNftCollection, listNftsByOwner, mintNftOps, transferNftOps, burnNftOps } from '../api/vexforge';
import { signAndSend } from '../lib/submit';

export default function NftCollection() {
  const { collection } = useParams();
  const { address, isConnected } = useWalletStore();
  const [collectionData, setCollectionData] = useState(null);
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMint, setShowMint] = useState(false);
  const [minting, setMinting] = useState(false);

  const [mintForm, setMintForm] = useState({
    tokenId: '',
    metadataUri: '',
    recipient: '',
  });

  const isCreator = isConnected && collectionData?.creator &&
    address?.toLowerCase() === collectionData.creator.toLowerCase();

  useEffect(() => {
    async function fetchData() {
      try {
        const colData = await getNftCollection(collection);
        setCollectionData(colData);

        // Fetch NFTs for display (all owners - use null owner to get all)
        try {
          const nftList = await listNftsByOwner(address || '0x0000000000000000000000000000000000000000', collection, 100);
          setNfts(Array.isArray(nftList) ? nftList : []);
        } catch {
          setNfts([]);
        }
      } catch (err) {
        console.error('Failed to fetch collection:', err);
      } finally {
        setLoading(false);
      }
    }
    if (collection) fetchData();
  }, [collection, address]);

  const handleMint = async (e) => {
    e.preventDefault();
    setMinting(true);
    try {
      const recipient = mintForm.recipient || address;
      const ops = mintNftOps(collection, mintForm.tokenId, mintForm.metadataUri, recipient);
      const txHash = await signAndSend(ops, `Mint NFT #${mintForm.tokenId} of ${collectionData?.symbol || 'collection'}`);
      if (!txHash) return;
      toast.success(`NFT mint submitted (tx ${String(txHash).slice(0, 14)}…)`);
      setShowMint(false);
      setMintForm({ tokenId: '', metadataUri: '', recipient: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to mint NFT');
    } finally {
      setMinting(false);
    }
  };

  const handleTransfer = async (tokenId, to) => {
    try {
      const txHash = await signAndSend(transferNftOps(collection, tokenId, to), `Transfer NFT #${tokenId}`);
      if (txHash) toast.success(`NFT transfer submitted (tx ${String(txHash).slice(0, 14)}…)`);
    } catch (err) {
      toast.error(err.message || 'Transfer failed');
    }
  };

  const handleBurn = async (tokenId) => {
    try {
      const txHash = await signAndSend(burnNftOps(collection, tokenId), `BURN NFT #${tokenId} (permanent)`);
      if (txHash) toast.success(`NFT burn submitted (tx ${String(txHash).slice(0, 14)}…)`);
    } catch (err) {
      toast.error(err.message || 'Burn failed');
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-gray-400">Loading collection...</div>;
  }

  if (!collectionData) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-white mb-2">Collection Not Found</h1>
        <p className="text-gray-400">Could not load collection {collection}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Banner + Header */}
      <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 overflow-hidden">
        <div className="h-40 bg-gradient-vextaris flex items-center justify-center">
          {collectionData.banner_uri ? (
            <img src={collectionData.banner_uri} alt="Banner" className="w-full h-full object-cover" />
          ) : (
            <PhotoIcon className="w-16 h-16 text-white/30" />
          )}
        </div>
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              {collectionData.icon_uri ? (
                <img src={collectionData.icon_uri} alt="Icon" className="w-16 h-16 rounded-xl object-cover -mt-12 border-4 border-vextaris-dark" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gradient-vextaris flex items-center justify-center -mt-12 border-4 border-vextaris-dark text-xl font-bold text-white">
                  {(collectionData.symbol || '?')[0]}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-white">{collectionData.name}</h1>
                <p className="text-vextaris-blue font-mono text-sm">{collectionData.symbol}</p>
              </div>
            </div>

            {isCreator && (
              <button
                onClick={() => setShowMint(!showMint)}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-vextaris text-white rounded-lg text-sm font-medium hover:opacity-90"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Mint NFT</span>
              </button>
            )}
          </div>

          {collectionData.description && (
            <p className="text-gray-400 mt-4">{collectionData.description}</p>
          )}

          <div className="flex space-x-6 mt-4 text-sm">
            <div>
              <span className="text-gray-400">Royalty: </span>
              <span className="text-white">{(collectionData.royalty_bps || 0) / 100}%</span>
            </div>
            {collectionData.max_supply && (
              <div>
                <span className="text-gray-400">Max Supply: </span>
                <span className="text-white">{collectionData.max_supply}</span>
              </div>
            )}
            <div>
              <span className="text-gray-400">Creator: </span>
              <span className="text-white font-mono text-xs">{collectionData.creator ? `${collectionData.creator.slice(0, 10)}...${collectionData.creator.slice(-6)}` : '--'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mint Form */}
      {showMint && (
        <form onSubmit={handleMint} className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white">Mint New NFT</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Token ID</label>
              <input type="number" value={mintForm.tokenId} onChange={e => setMintForm(p => ({ ...p, tokenId: e.target.value }))} required min="0"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white focus:outline-none focus:border-vextaris-purple" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Metadata URI</label>
              <input type="text" value={mintForm.metadataUri} onChange={e => setMintForm(p => ({ ...p, metadataUri: e.target.value }))} required
                placeholder="ipfs://Qm..." className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple font-mono text-sm" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Recipient (optional)</label>
              <input type="text" value={mintForm.recipient} onChange={e => setMintForm(p => ({ ...p, recipient: e.target.value }))}
                placeholder="Defaults to you" className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple font-mono text-sm" />
            </div>
          </div>
          <button type="submit" disabled={minting}
            className="px-6 py-2 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50">
            {minting ? 'Minting...' : 'Mint'}
          </button>
        </form>
      )}

      {/* NFT Grid */}
      {nfts.length === 0 ? (
        <div className="bg-vextaris-dark/50 rounded-2xl border border-vextaris-purple/20 p-12 text-center">
          <PhotoIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">No NFTs in this collection yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {nfts.map((nft, i) => {
            const isOwner = isConnected && address?.toLowerCase() === nft.owner?.toLowerCase();
            return (
              <div key={i} className="bg-vextaris-dark/50 backdrop-blur-xl rounded-xl border border-vextaris-purple/20 overflow-hidden">
                <div className="aspect-square bg-gradient-vextaris flex items-center justify-center">
                  <span className="text-4xl font-bold text-white/70">#{nft.token_id}</span>
                </div>
                <div className="p-3 space-y-2">
                  <p className="text-white text-sm font-medium">Token #{nft.token_id}</p>
                  <p className="text-xs text-gray-400 font-mono truncate">
                    Owner: {nft.owner ? `${nft.owner.slice(0, 8)}...${nft.owner.slice(-6)}` : '--'}
                  </p>
                  {isOwner && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          const to = prompt('Transfer to address:');
                          if (to) handleTransfer(nft.token_id, to);
                        }}
                        className="flex-1 flex items-center justify-center space-x-1 px-2 py-1 bg-vextaris-purple/10 text-vextaris-blue rounded text-xs hover:bg-vextaris-purple/20"
                      >
                        <PaperAirplaneIcon className="w-3 h-3" />
                        <span>Transfer</span>
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Burn this NFT? This cannot be undone.')) handleBurn(nft.token_id);
                        }}
                        className="flex items-center justify-center px-2 py-1 bg-red-500/10 text-red-400 rounded text-xs hover:bg-red-500/20"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}